import { useEffect, type ReactNode } from "react";
import { AlertTriangle, Inbox, RotateCcw } from "lucide-react";
import { ApiError } from "../api/client";
import { describeError } from "../lib/errorWording";
import { useRelease150 } from "../lib/release150";

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  useEffect(() => {
    document.title = `${title} | Summit Operations`;
    window.dispatchEvent(new CustomEvent("summitops:print-title", { detail: title }));
  }, [title]);
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 pb-4">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold tracking-tight text-slate-900">{title}</h1>
        {subtitle && <div className="mt-0.5 text-sm text-slate-500">{subtitle}</div>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function ErrorBanner({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const release150 = useRelease150();
  if (release150) return <PlainErrorBanner error={error} onRetry={onRetry} />;
  const api = error instanceof ApiError ? error : null;
  return (
    <div role="alert" className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
      <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 flex-none" />
      <div className="min-w-0 flex-1">
        <p className="font-medium">{api?.title ?? "Something went wrong loading this view."}</p>
        {api?.message && api.message !== api.title && <p className="mt-0.5 text-red-700">{api.message}</p>}
        {api?.correlationId && <p className="mt-1 font-mono text-xs text-red-700">Reference: {api.correlationId}</p>}
      </div>
      {onRetry && (
        <button type="button" className="btn-secondary inline-flex items-center gap-1" onClick={onRetry}>
          <RotateCcw aria-hidden="true" className="h-3.5 w-3.5" />
          Retry
        </button>
      )}
    </div>
  );
}

/**
 * Release 1.5.0 (O-R68): the same banner in the plain wording — what happened, what to do, the server's own sentence
 * only when it is safe, and the reference for support. Never server internals, never a raw status code.
 */
function PlainErrorBanner({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const w = describeError(error);
  return (
    <div role="alert" className="flex flex-wrap items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-error-kind={w.kind}>
      <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 flex-none" />
      <div className="min-w-0 flex-1">
        <p className="font-medium" data-testid="error-title">
          {w.title}
        </p>
        {w.detail && <p className="mt-0.5 break-words text-red-800">{w.detail}</p>}
        <p className="mt-0.5 text-red-800" data-testid="error-action">
          {w.action}
        </p>
        {w.reference && (
          <p className="mt-1 break-all font-mono text-xs text-red-800" data-testid="error-reference">
            Reference: {w.reference}
          </p>
        )}
      </div>
      {onRetry && (
        <button type="button" className="btn-secondary inline-flex items-center gap-1" onClick={onRetry}>
          <RotateCcw aria-hidden="true" className="h-3.5 w-3.5" />
          Try again
        </button>
      )}
    </div>
  );
}

/**
 * The one empty state for every list and board: an icon, what the emptiness means in plain words, and (optionally)
 * the next useful action — pass `action` only when the person's role allows it.
 */
export function EmptyState({
  title,
  children,
  icon: Icon = Inbox,
  action,
  compact = false,
  testId
}: {
  title: string;
  children?: ReactNode;
  icon?: typeof Inbox;
  action?: ReactNode;
  compact?: boolean;
  testId?: string;
}) {
  return (
    <div
      className={`flex flex-col items-center justify-center gap-2 text-center ${compact ? "px-3 py-4" : "px-6 py-10"}`}
      data-testid={testId ?? "empty-state"}
    >
      <Icon aria-hidden="true" className={compact ? "h-5 w-5 text-slate-500" : "h-6 w-6 text-slate-500"} />
      <p className="text-sm font-medium text-slate-700">{title}</p>
      {children && <div className="max-w-md text-xs text-slate-600">{children}</div>}
      {action && <div className="mt-1 flex flex-wrap items-center justify-center gap-2">{action}</div>}
    </div>
  );
}

/**
 * Release 1.5.0 polish: one table row that says why a table is empty, under the table's own headings. Shown only when
 * the table has no rows and only with release 1.5.0 on (1.4.0 keeps its bare table).
 */
export function EmptyTableRow({ colSpan, rows, children }: { colSpan: number; rows: number; children: ReactNode }) {
  const release150 = useRelease150();
  if (!release150 || rows > 0) return null;
  return (
    <tr data-testid="empty-table-row">
      <td colSpan={colSpan} className="py-4 text-center text-xs text-slate-600">
        {children}
      </td>
    </tr>
  );
}

/**
 * The design system's `.table-scroll` wrapper, made keyboard reachable: a labelled region with its own tab stop, so
 * a wide table can be scrolled with the arrow keys and never clips a column silently. Every data table in the
 * application goes through this — a column a mouse-less reader cannot reach is the same as a missing column.
 */
export function TableScroll({ label, className = "", children }: { label: string; className?: string; children: ReactNode }) {
  return (
    <div className={`table-scroll ${className}`} role="region" aria-label={label} tabIndex={0}>
      {children}
    </div>
  );
}

/** A card with a heading, used for every panel on a hub page so the spacing is identical everywhere. */
export function Panel({
  title,
  description,
  actions,
  children,
  className = ""
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`card flex min-w-0 flex-col gap-3 !p-4 ${className}`} aria-label={typeof title === "string" ? title : undefined}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold tracking-tight text-slate-900">{title}</h2>
          {description && <div className="mt-0.5 text-xs leading-snug text-slate-500">{description}</div>}
        </div>
        {actions && <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </section>
  );
}
